import React, { useEffect } from "react";
import { useTranslate, useCustom, useCustomMutation } from "@refinedev/core";
import { Form, Row, Col, Select, InputNumber, Switch, Button, Spin, Alert, message } from "antd";

type UnitType = "order_completed" | "decrypt_completed" | "file_assembled";

interface UnitPricingFormItem {
    unitType?: UnitType;
    bundleSize?: number | null;
    bundleFee?: number | null;
    unitPrice?: number;
    currency?: string;
    isActive?: boolean;
}

interface TenantPricingResponseDto {
    tenantId: number;
    tenantName: string;
    pricing: UnitPricingFormItem[];
}

interface TenantPricingTabProps {
    tenantId?: number;
    isSuperAdmin: boolean;
    active: boolean;
}

interface PricingFormValues {
    pricing?: UnitPricingFormItem[];
}

const DEFAULT_CURRENCY = "EUR";

export const TenantPricingTab: React.FC<TenantPricingTabProps> = ({ tenantId, isSuperAdmin, active }) => {
    const t = useTranslate();
    const [form] = Form.useForm<PricingFormValues>();

    const {
        data: pricingResponse,
        isLoading: pricingLoading,
        error: pricingError,
        refetch: refetchPricing,
    } = useCustom<{ data: TenantPricingResponseDto }>({
        url: tenantId ? `tenant-billing/pricing/${tenantId}` : "",
        method: "get",
        queryOptions: {
            enabled: !!tenantId && isSuperAdmin && active,
            retry: false,
        },
    });

    const { mutate: updatePricing, isLoading: savingPricing } = useCustomMutation();

    useEffect(() => {
        const apiData = pricingResponse?.data;
        if (!apiData || !Array.isArray(apiData.pricing)) {
            return;
        }

        const mapped: UnitPricingFormItem[] = apiData.pricing.map((item) => ({
            unitType: item.unitType,
            bundleSize: item.bundleSize ?? null,
            bundleFee: item.bundleFee ?? null,
            unitPrice: item.unitPrice,
            currency: item.currency || DEFAULT_CURRENCY,
            isActive: typeof item.isActive === "boolean" ? item.isActive : true,
        }));

        form.setFieldsValue({ pricing: mapped });
    }, [pricingResponse, form]);

    const handleSavePricing = async () => {
        if (!tenantId) {
            message.error(
                t(
                    "tenants.pricing.errors.missingTenant",
                    "Le tenant doit être chargé avant de sauvegarder la tarification.",
                ),
            );
            return;
        }

        try {
            const values = await form.validateFields();
            const pricing = (values.pricing || []).filter(
                (item): item is UnitPricingFormItem => !!item && !!item.unitType,
            );

            const seen = new Set<UnitType>();
            for (const item of pricing) {
                if (!item.unitType) continue;
                if (seen.has(item.unitType)) {
                    message.error(
                        t(
                            "tenants.pricing.errors.duplicateUnitType",
                            "Vous ne pouvez avoir qu'une règle par type d'unité.",
                        ),
                    );
                    return;
                }
                seen.add(item.unitType);
            }

            const normalized = pricing.map((item) => ({
                unitType: item.unitType,
                bundleSize:
                    item.bundleSize === undefined || item.bundleSize === null || Number.isNaN(item.bundleSize)
                        ? null
                        : item.bundleSize,
                bundleFee:
                    item.bundleFee === undefined || item.bundleFee === null || Number.isNaN(item.bundleFee)
                        ? null
                        : item.bundleFee,
                unitPrice: item.unitPrice ?? 0,
                currency: item.currency || DEFAULT_CURRENCY,
                isActive: typeof item.isActive === "boolean" ? item.isActive : true,
            }));

            updatePricing(
                {
                    url: `tenant-billing/pricing/${tenantId}`,
                    method: "put",
                    values: { pricing: normalized },
                },
                {
                    onSuccess: () => {
                        message.success(
                            t(
                                "tenants.pricing.messages.saveSuccess",
                                "Configuration tarifaire sauvegardée avec succès.",
                            ),
                        );
                        refetchPricing();
                    },
                    onError: () => {
                        message.error(
                            t(
                                "tenants.pricing.messages.saveError",
                                "Échec de la sauvegarde de la configuration tarifaire.",
                            ),
                        );
                    },
                },
            );
        } catch (error) {
            message.error(
                t(
                    "tenants.pricing.errors.validationFailed",
                    "Merci de corriger les erreurs dans le formulaire de tarification.",
                ),
            );
        }
    };

    const unitTypeOptions: { label: string; value: UnitType }[] = [
        {
            value: "order_completed",
            label: t("tenants.pricing.unitTypes.order_completed", "Commande terminée"),
        },
        {
            value: "decrypt_completed",
            label: t("tenants.pricing.unitTypes.decrypt_completed", "Déchiffrement terminé"),
        },
        {
            value: "file_assembled",
            label: t("tenants.pricing.unitTypes.file_assembled", "Fichier assemblé"),
        },
    ];

    const renderContent = () => {
        if (!isSuperAdmin) {
            return (
                <Alert
                    type="warning"
                    message={t(
                        "tenants.pricing.errors.accessDenied",
                        "Seul un super administrateur peut gérer la tarification du tenant.",
                    )}
                />
            );
        }

        if (!tenantId) {
            return (
                <Alert
                    type="info"
                    message={t(
                        "tenants.pricing.messages.waitForTenant",
                        "La tarification sera disponible une fois le tenant chargé.",
                    )}
                />
            );
        }

        return (
            <Form form={form} layout="vertical">
                {pricingError && (
                    <div className="mb-4">
                        <Alert
                            type="error"
                            message={t(
                                "tenants.pricing.errors.loadFailed",
                                "Impossible de charger la configuration tarifaire.",
                            )}
                        />
                    </div>
                )}

                <Form.List name="pricing">
                    {(fields, { add, remove }) => {
                        const currentValues = form.getFieldValue("pricing") as UnitPricingFormItem[] | undefined;
                        const usedTypes = (currentValues || [])
                            .map((item) => item?.unitType)
                            .filter((v): v is UnitType => !!v);

                        const handleAddRule = () => {
                            if (usedTypes.length >= unitTypeOptions.length) {
                                return;
                            }
                            add({
                                unitType: undefined,
                                bundleSize: null,
                                bundleFee: null,
                                unitPrice: undefined,
                                currency: DEFAULT_CURRENCY,
                                isActive: true,
                            });
                        };

                        return (
                            <>
                                {fields.length === 0 && (
                                    <Alert
                                        className="mb-4"
                                        type="info"
                                        message={t(
                                            "tenants.pricing.messages.noRules",
                                            "Aucune règle de tarification configurée pour ce tenant.",
                                        )}
                                    />
                                )}

                                {fields.map((field, index) => {
                                    const rowValues = currentValues || [];
                                    const thisRow = rowValues[index];
                                    const thisUnitType = thisRow?.unitType;

                                    const optionsWithDisabled = unitTypeOptions.map((opt) => ({
                                        ...opt,
                                        disabled:
                                            opt.value !== thisUnitType &&
                                            usedTypes.includes(opt.value as UnitType),
                                    }));

                                    return (
                                        <div
                                            key={field.key}
                                            className="mb-4 rounded border border-gray-200 bg-gray-50 p-4 shadow-sm flex flex-col gap-4"
                                        >
                                            <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-3">
                                                <span className="text-sm font-medium text-gray-700">
                                                    {t(
                                                        "tenants.pricing.messages.ruleTitle",
                                                        "Règle de tarification {{index}}",
                                                    ).replace("{{index}}", String(index + 1))}
                                                </span>
                                            </div>
                                            <Row gutter={[16, 16]}>
                                                <Col xs={24} sm={12} md={8}>
                                                    <Form.Item
                                                        {...field}
                                                        name={[field.name, "unitType"]}
                                                        label={t(
                                                            "tenants.pricing.fields.unitType",
                                                            "Type d'unité facturable",
                                                        )}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t(
                                                                    "tenants.pricing.errors.unitTypeRequired",
                                                                    "Le type d'unité est obligatoire.",
                                                                ),
                                                            },
                                                        ]}
                                                    >
                                                        <Select options={optionsWithDisabled} />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12} md={8}>
                                                    <Form.Item
                                                        name={[field.name, "bundleSize"]}
                                                        label={t(
                                                            "tenants.pricing.fields.bundleSize",
                                                            "Taille du forfait (optionnel)",
                                                        )}
                                                    >
                                                        <InputNumber
                                                            className="w-full"
                                                            placeholder="100"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12} md={8}>
                                                    <Form.Item
                                                        name={[field.name, "bundleFee"]}
                                                        label={t(
                                                            "tenants.pricing.fields.bundleFee",
                                                            "Montant du forfait (optionnel)",
                                                        )}
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            step={0.01}
                                                            precision={2}
                                                            className="w-full"
                                                            placeholder="49.90"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 16]}>
                                                <Col xs={24} sm={12} md={8}>
                                                    <Form.Item
                                                        name={[field.name, "unitPrice"]}
                                                        label={t(
                                                            "tenants.pricing.fields.unitPrice",
                                                            "Prix unitaire",
                                                        )}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t(
                                                                    "tenants.pricing.errors.unitPriceRequired",
                                                                    "Le prix unitaire est obligatoire.",
                                                                ),
                                                            },
                                                        ]}
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            step={0.01}
                                                            precision={4}
                                                            className="w-full"
                                                            placeholder="0.60"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12} md={8}>
                                                    <Form.Item
                                                        name={[field.name, "currency"]}
                                                        label={t(
                                                            "tenants.pricing.fields.currency",
                                                            "Devise",
                                                        )}
                                                    >
                                                        <Select
                                                            options={[
                                                                { label: "EUR", value: "EUR" },
                                                                { label: "USD", value: "USD" },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12} md={4}>
                                                    <Form.Item
                                                        name={[field.name, "isActive"]}
                                                        label={t(
                                                            "tenants.pricing.fields.isActive",
                                                            "Actif",
                                                        )}
                                                        valuePropName="checked"
                                                    >
                                                        <Switch />
                                                    </Form.Item>
                                                </Col>
                                                <Col
                                                    xs={24}
                                                    sm={12}
                                                    md={4}
                                                    className="flex items-end justify-end"
                                                >
                                                    <Button danger onClick={() => remove(field.name)}>
                                                        {t(
                                                            "tenants.pricing.actions.removeRule",
                                                            "Supprimer la règle",
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    );
                                })}

                                <Button
                                    type="dashed"
                                    onClick={handleAddRule}
                                    disabled={usedTypes.length >= unitTypeOptions.length}
                                    className="mt-2"
                                >
                                    {t(
                                        "tenants.pricing.actions.addRule",
                                        "Ajouter une règle de tarification",
                                    )}
                                </Button>
                            </>
                        );
                    }}
                </Form.List>

                <div className="mt-6 flex justify-end">
                    <Button
                        type="primary"
                        onClick={handleSavePricing}
                        loading={savingPricing}
                    >
                        {t("tenants.pricing.actions.save", "Enregistrer la tarification")}
                    </Button>
                </div>
            </Form>
        );
    };

    return (
        <Spin spinning={pricingLoading || savingPricing}>
            <div className="px-2 py-2">{renderContent()}</div>
        </Spin>
    );
};

